import { Box, Grid2, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Pill, PillVariants, Skeleton } from '@/components';
import { Routes, urls } from '@/constants';
import { CheckmarkIcon } from '@/icons';
import { questionAnswer } from '@/images';
import { AnalyticsService, EVENTS } from '@/services';
import {
  selectPlan,
  selectSessionUserId,
  selectUserRegion,
  useAppSelector,
  useCreateCheckoutSessionMutation,
  useFetchDefaultPromoCodeQuery,
  useFetchPricingTableValuesQuery,
  useFetchUserStripeCustomerInfoQuery,
  useGenerateBillingPortalLinkMutation,
  useLazyPollForCheckoutSessionUrlQuery,
} from '@/store';
import { PricingTableCTA } from '@/types';
import {
  getAdAttributionCookies,
  getRedirectSearchParams,
  getSymbolForCurrency,
  SnackbarUtils,
} from '@/utils';

const TEAM_SUB_PRODUCT_ID = 'prod_T4GfLk6POMzRuG';

export const PricingTable = () => {
  const navigate = useNavigate();
  const userId = useAppSelector(selectSessionUserId);
  const region = useAppSelector(selectUserRegion);
  const { data: plan } = useAppSelector(selectPlan);

  const attributionMetadata = useMemo(() => getAdAttributionCookies(), []);

  const { data: pricingTableData, isLoading: isPricingTableDataLoading } =
    useFetchPricingTableValuesQuery({ region, metadata: attributionMetadata });
  const { highlighted_plan: highlightedPlan } = Object.fromEntries(
    new URLSearchParams(location.search).entries(),
  );

  const { data: stripeData } = useFetchUserStripeCustomerInfoQuery();
  const hasCurrentStripeSubscription = stripeData?.hasCurrentStripeSubscription;

  const [createCheckoutSession, { isLoading: isCheckoutSessionLoading }] =
    useCreateCheckoutSessionMutation();
  const [
    pollForCheckoutSessionUrl,
    { isLoading: isPollForCheckoutSessionUrlLoading },
  ] = useLazyPollForCheckoutSessionUrlQuery();

  const [
    generateBillingPortalLink,
    { isLoading: isBillingPortalLinkGenerating },
  ] = useGenerateBillingPortalLinkMutation();

  const { data: promoCode } = useFetchDefaultPromoCodeQuery({ region });

  const activePromoCode =
    promoCode?.expiresAt && new Date() > new Date(promoCode.expiresAt)
      ? null
      : promoCode;

  const products = useMemo(
    () =>
      pricingTableData?.map(product => {
        const isHighlight =
          (product.isHighlight && !highlightedPlan) ||
          product.name.toLowerCase() === highlightedPlan?.toLowerCase();

        const price = Math.ceil(
          (activePromoCode
            ? (product.amount ?? 0) * (1 - activePromoCode.percentOff! / 100)
            : (product.amount ?? 0)) / 100,
        );
        const crossedOutPrice = activePromoCode
          ? Math.ceil((product.amount ?? 0) / 100)
          : null;

        return {
          ...product,
          price,
          crossedOutPrice,
          isHighlight,
        };
      }) ?? [],
    [pricingTableData, activePromoCode, highlightedPlan],
  );

  const isCheckoutLoading =
    isCheckoutSessionLoading ||
    isPollForCheckoutSessionUrlLoading ||
    isBillingPortalLinkGenerating;

  const onCtaClick = async ({
    stripeProductId,
    callToAction,
    productName,
  }: {
    stripeProductId: string;
    callToAction: PricingTableCTA;
    productName: string;
  }) => {
    AnalyticsService.logEvent(EVENTS.PRICING_EVENTS.CTA_CLICKED, {
      callToAction,
      stripeProductId,
    });
    if (callToAction === 'contact') {
      window.open(urls.bookADemo[region], '_blank');
    } else if (!userId) {
      navigate({
        pathname: Routes.REGISTER,
        search: getRedirectSearchParams(),
      });
      return;
    } else if (
      hasCurrentStripeSubscription &&
      stripeProductId !== TEAM_SUB_PRODUCT_ID &&
      productName !== 'Team'
    ) {
      // If user has active sub and they try to purchase any subscription (except Team), send them to billing portal instead of checkout page
      try {
        AnalyticsService.logEvent(
          EVENTS.PRICING_EVENTS.REDIRECT_CHECKOUT_TO_BILLING_PORTAL,
          {
            callToAction,
            stripeProductId,
          },
        );
        const { url } = await generateBillingPortalLink().unwrap();
        window.open(url, '_self');
      } catch {
        SnackbarUtils.error(
          'An error occurred while generating the billing portal link. Please try again later.',
        );
      }
    } else {
      try {
        AnalyticsService.logEvent(EVENTS.PRICING_EVENTS.CHECKOUT_STARTED, {
          callToAction,
          stripeProductId,
        });
        const { checkoutSessionTaskId } = await createCheckoutSession({
          stripeProductId,
          country: region,
          organizationId: plan?.organizationId,
          redirectUrl: `${window.location.origin}${Routes.PURCHASE_SUCCESS}`,
        }).unwrap();
        const { url } = await pollForCheckoutSessionUrl({
          checkoutSessionTaskId,
        }).unwrap();
        window.open(url, '_self');
      } catch {
        AnalyticsService.logEvent(EVENTS.PRICING_EVENTS.CHECKOUT_FAILURE, {
          callToAction,
          stripeProductId,
        });
        SnackbarUtils.error(
          'Failed to initiate checkout. Please contact support.',
        );
      }
    }
  };

  if (isPricingTableDataLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
        }}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            sx={{ borderRadius: 4, flex: 1 }}
            height={500}
          />
        ))}
      </Box>
    );
  }

  return (
    <Grid2 container spacing={3}>
      {products.map(product => (
        <Grid2
          key={product.name}
          size={{
            lg: 3,
            sm: 6,
            xs: 12,
          }}
          sx={[
            {
              bgcolor: 'surface.background',
              borderRadius: 4,
              p: 3,
              boxShadow: '3px 3px 10px 0 rgba(0, 0, 0, 0.20)',
              minWidth: 200,
              position: 'relative',
            },
            product.isHighlight
              ? {
                  border: theme =>
                    `2.5px solid ${theme.palette.secondary.main}`,
                }
              : {},
          ]}>
          {product.isHighlight && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 100,
                height: 23,
                top: -1,
                bgcolor: 'secondary.main',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Typography variant="caption1Bold" color="text.contrast">
                {product.isHighlight ? 'Most Popular' : 'Recommended'}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              minHeight: 150,
              display: 'flex',
              flexDirection: 'column',
            }}>
            <Typography variant="h2">{product.name}</Typography>
            <Typography variant="body3SemiBold" sx={{ maxWidth: 160 }}>
              {product.productDescription}
            </Typography>
            <Box sx={{ height: activePromoCode ? 120 : 106, mt: 0.5 }}>
              {product.callToAction !== 'contact' ? (
                <>
                  {product.crossedOutPrice && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography
                        variant="h3"
                        sx={{
                          textDecoration: 'line-through',
                          color: 'text.light',
                        }}>
                        {product &&
                          `${getSymbolForCurrency(product.currency)}${product.crossedOutPrice}`}
                      </Typography>
                      <Pill
                        variant={PillVariants.INFO_PURPLE}
                        label={`Save ${activePromoCode?.percentOff}%`}
                      />
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 1.5,
                      alignItems: 'center',
                      mt: 'auto',
                    }}>
                    <Typography variant="hero4">
                      {product.price &&
                        `${getSymbolForCurrency(product.currency)}${product.price}`}
                    </Typography>
                    {product.recurringInterval === 'year' && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}>
                        {product.name === 'Team' && (
                          <Typography variant="body3SemiBold">
                            starting at
                          </Typography>
                        )}
                        <Typography variant="body3SemiBold">
                          {`${product.currency?.toLocaleUpperCase()} ${product.name === 'Team' ? 'per user' : 'per year'}`}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box
                    sx={{
                      width: 160,
                      height: 40,
                      p: '2 1 2 1',
                      border: theme =>
                        `3px solid ${theme.palette.secondary.main}80`,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mt: 1,
                    }}>
                    <Typography variant="h4">
                      {product.usageLimit
                        ? `${product.usageLimit} ${region === 'USA' ? 'Credits CPE' : 'Hours CPD'}`
                        : `Unlimited ${region === 'USA' ? 'CPE' : 'CPD'}`}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    mt: 0.5,
                  }}>
                  <Box component={'img'} src={questionAnswer} />
                  <Typography variant="body2SemiBold" sx={{ display: 'block' }}>
                    Custom Pricing
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              mt: 2,
              mb: 2,
              width: '100%',
              bgcolor: 'black',
              borderBottom: '0.5px solid',
            }}
          />
          <Button
            skinny={false}
            label={
              product.callToAction === 'contact' ? 'Book A Demo' : 'Subscribe'
            }
            isLoading={isCheckoutLoading}
            onClick={() =>
              onCtaClick({
                stripeProductId: product.stripeProductId!,
                callToAction: product.callToAction,
                productName: product.name,
              })
            }
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              mt: 2,
            }}>
            {product.featureList.map((feature, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1,
                  alignItems: 'center',
                }}>
                <CheckmarkIcon
                  sx={{
                    color: 'secondary.main',
                    width: 24,
                    height: 24,
                    p: 0.5,
                  }}
                />
                <Typography key={feature} variant="body3">
                  {feature}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid2>
      ))}
    </Grid2>
  );
};
